/*
 *   File : emergency-fund.js
 *   Author : https://evoqins.com
 *   Description : Emergency-fund goal page
 *   Integrations : react-toastify
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useState, useEffect } from "react";

// *********** Third Party ***************//
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//
import { CustomAmountInput, CustomSlider, SelectDropdown } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Footer } from "../../Component/Footer";
import { BreadCrumbs } from "../../Component/Other";
import { Icon } from "../../Component/Icon";
import { ChangeBasketNameModal } from "../../Component/Modal";
import GoalSummary from "./goal-summary";
import { APIService } from "../../Service";
import { _formatLac, _formatToRupees, _setGoal } from "../../Helper";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LeftPillarImage from "../../Assets/Images/GoalNew/goal-left-banner.png";
import RightPillarImage from "../../Assets/Images/GoalNew/goal-right-banner.png";

const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Life goals",
        path: "/goals"
    },
];

const DEFAULT_SALARY = 200000;
const DEFAULT_EMI = 40000;
const DEFAULT_SCHOOL_FEES = 15000;
const DEFAULT_MONTHLY_EXPENSES = 50000;
const DEFAULT_BUILD_MONTHS = 12;

const SALARY_MIN = 25000;
const MAX_SALARY = 100000000;
const SALARY_STEP = 25000;
const MAX_EMI = 1000000;
const EMI_STEP = 5000;
const MAX_SCHOOL_FEES = 1000000;
const SCHOOL_FEES_STEP = 2500;
const MONTHLY_EXPENSES_MIN = 10000;
const MAX_MONTHLY_EXPENSES = 1000000;
const MONTHLY_EXPENSES_STEP = 5000;

// The fund covers this many months of the must pay expenses
const EMERGENCY_COVER_MONTHS = 6;

// The monthly investment is rounded up to the next 100
const INVESTMENT_ROUNDING = 100;

const BUILD_MONTH_OPTIONS = [6, 12, 18, 24].map((months) => ({
    id: months,
    display_name: `${months} months`,
}));

// A saved goal may hold a build time that is not offered, it falls back to the default
const _getBuildMonthsOption = (months) => {
    const option = BUILD_MONTH_OPTIONS.find((build_option) => build_option.id === Number(months));

    return option ?? BUILD_MONTH_OPTIONS.find((build_option) => build_option.id === DEFAULT_BUILD_MONTHS);
};

// Month and year the fund is ready, e.g. "Oct 2027". The shared helper works in years, this goal in months
const _getAchieveByText = (months) => {
    if (months <= 0) {
        return "-";
    }

    let achieve_by = new Date();
    achieve_by.setMonth(achieve_by.getMonth() + months);

    return achieve_by.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const EmergencyFund = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [goalId, setGoalId] = useState("");
    const [goalDetail, setGoalDetail] = useState({});
    const [loading, setLoading] = useState(true);

    const [salary, setSalary] = useState(DEFAULT_SALARY);
    const [emi, setEmi] = useState(DEFAULT_EMI);
    const [schoolFees, setSchoolFees] = useState(DEFAULT_SCHOOL_FEES);
    const [monthlyExpenses, setMonthlyExpenses] = useState(DEFAULT_MONTHLY_EXPENSES);
    const [selectedBuildMonths, setSelectedBuildMonths] = useState(_getBuildMonthsOption(DEFAULT_BUILD_MONTHS));
    const [isSavingGoal, setIsSavingGoal] = useState(false);
    const [savedGoalId, setSavedGoalId] = useState(null);
    const [goalName, setGoalName] = useState("");
    const [showChangeNameModal, setShowChangeNameModal] = useState(false);
    const [isNameUpdating, setIsNameUpdating] = useState(false);
    const [goalSummary, setGoalSummary] = useState(false);

    useEffect(() => {

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (location.state !== null) {
            setGoalId(location.state.goal_id);
        } else {
            navigate("/goals");
        };

        // Back from the summary step
        if (location.state?.is_summary === false) {
            setGoalSummary(false);
        }

    }, [location]);

    useEffect(() => {
        if (goalId !== "") {
            _getGoalDetails();
        }
    }, [goalId]);

    // The summary step is kept in the url, so that a reload stays on it
    useEffect(() => {
        if (goalSummary === true) {
            navigate(`${location.pathname}#configure`, {
                state: {
                    goal_id: goalId,
                }
            });
        }
    }, [goalSummary]);

    useEffect(() => {
        if (showChangeNameModal) {
            new Modal(document.getElementById("change-basket-name-modal")).show();
        }
    }, [showChangeNameModal]);

    const breadcrumbs = [...BREADCRUMBS, { label: goalDetail.admin_configured?.name }];

    const salaryAmount = Number(salary) || 0;
    const buildMonthsCount = selectedBuildMonths.id;

    // Must pay expenses are what has to be paid every month, even without an income
    const mustPayExpenses = (Number(emi) || 0) + (Number(schoolFees) || 0) + (Number(monthlyExpenses) || 0);
    const emergencyCorpus = mustPayExpenses * EMERGENCY_COVER_MONTHS;

    const monthlyInvestment = Math.ceil(emergencyCorpus / buildMonthsCount / INVESTMENT_ROUNDING) * INVESTMENT_ROUNDING;

    const monthlySurplus = Math.max(salaryAmount - mustPayExpenses, 0);
    const isSurplusEnough = monthlyInvestment <= monthlySurplus;

    const emergencyCorpusText = _formatLac(emergencyCorpus);
    const achieveByText = _getAchieveByText(buildMonthsCount);

    //handle back
    const _handleBack = () => {
        navigate("/goals");
    };

    const _handleSalaryChange = (value) => {
        setSalary(value);
    };

    const _handleEmiChange = (value) => {
        setEmi(value);
    };

    const _handleSchoolFeesChange = (value) => {
        setSchoolFees(value);
    };

    const _handleMonthlyExpensesChange = (value) => {
        setMonthlyExpenses(value);
    };

    const _handleSelectBuildMonths = (option) => {
        setSelectedBuildMonths(option);
    };

    const _handleChangeGoalName = (name) => {
        _renameGoal(name, false);
    };

    const _handleSkipGoalName = () => {
        _renameGoal("", true);
    };

    const _handleCloseChangeNameModal = () => {
        setIsNameUpdating(false);
        setShowChangeNameModal(false);
    };

    // API - Rename goal
    const _renameGoal = (name, skip) => {
        let api_version = 3;
        let is_post = true;
        let url = "goal/rename";
        let data = {
            "goal_id": savedGoalId,
            "skip": true,
        };

        if (skip === false) {
            data = {
                "goal_id": savedGoalId,
                "name": name,
                "skip": false,
            };
        }

        setIsNameUpdating(true);

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                if (skip === false) {
                    setGoalName(name);
                }
                document.getElementById("change-basket-name-modal-close")?.click();
                setGoalSummary(true);
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsNameUpdating(false);
        });
    };

    // API - Save the emergency fund goal, the monthly investment goes as the SIP
    const _handleSaveGoal = () => {
        if (mustPayExpenses <= 0) {
            toast.dismiss();
            toast.error("Enter your EMI, school fees or monthly expenses to calculate your emergency fund", { className: "e-toast" });
            return;
        }

        const params = {
            "goal_id": goalId,
            "target": emergencyCorpus,
            "sip": monthlyInvestment,
            "lumpsum": null,
            "sip_tenure": Math.ceil(buildMonthsCount / 12),
            "lumpsum_select": 1,
            "inflation_select": false,
            "additional_data": {
                "salary": salaryAmount,
                "emi": Number(emi) || 0,
                "school_fees": Number(schoolFees) || 0,
                "monthly_expenses": Number(monthlyExpenses) || 0,
                "funds_required_months": buildMonthsCount,
            },
        };

        setIsSavingGoal(true);

        _setGoal(params).then((response) => {
            if (response.status_code === 200) {
                setSavedGoalId(response.data.goal_id);
                setShowChangeNameModal(true);
                toast.dismiss();
                toast.success("Goal saved", { className: "e-toast" });
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsSavingGoal(false);
        });
    };

    //API- Post goal detail
    const _getGoalDetails = () => {
        let is_post = true;
        let url = 'goal/get';
        let data = {
            "goal_id": goalId,
            "is_invested": false
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setGoalDetail(response.data);
                _fillGoalInputs(response.data);

                // Preserving current step if user reloads page
                if (location.hash) {
                    setGoalSummary(true);
                }
            } else {
                setGoalDetail({});
            }
            setLoading(false);
        });
    };

    // Fills the inputs from the saved goal (user_configured), the rest fall back to the defaults
    const _fillGoalInputs = (goal) => {
        const saved_inputs = goal.user_configured?.additional_data || {};

        setSalary(saved_inputs.salary ?? DEFAULT_SALARY);
        setEmi(saved_inputs.emi ?? DEFAULT_EMI);
        setSchoolFees(saved_inputs.school_fees ?? DEFAULT_SCHOOL_FEES);
        setMonthlyExpenses(saved_inputs.monthly_expenses ?? DEFAULT_MONTHLY_EXPENSES);
        setSelectedBuildMonths(_getBuildMonthsOption(saved_inputs.funds_required_months));
    };

    // Stays rendered on the summary step until the modal has finished closing
    const changeNameModal = showChangeNameModal &&
        <ChangeBasketNameModal title="Change goal name"
            skipButtonName="Skip"
            basketName={goalName || goalDetail.admin_configured?.name || ""}
            loading={isNameUpdating}
            onChangeName={_handleChangeGoalName}
            onSkip={_handleSkipGoalName}
            onClose={_handleCloseChangeNameModal} />;

    // Goal summary step
    if (goalSummary) {
        return (
            <>
                <GoalSummary goal_name={goalDetail.admin_configured?.short_name}
                    savedGoalId={savedGoalId}
                    customGoalName={goalName}
                    isLumpsum={false}
                    tenure={goalDetail.admin_configured?.meta_data?.tenure}
                    inflationSelect={false} />

                {changeNameModal}

                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">

                {loading ?
                    <div className="min-h-100vh text-center mt-5" >
                        <img src={Loader}
                            draggable="false"
                            alt="loader" />
                    </div>
                    :
                    <div className="col-lg-11 p-0 pb-4 pb-sm-5">
                        {/* breadcrumbs */}
                        <BreadCrumbs data={breadcrumbs} />

                        {/* Page title */}
                        <header className="d-flex align-items-center gap-sm-3 gap-2 my-sm-4 my-3 ">
                            <button type="button"
                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                aria-label="Back to life goals"
                                onClick={_handleBack}>
                                <Icon icon="arrow-back-left" size={24} className="width-20px-res" />
                            </button>
                            <h1 className="mb-0 e-text-chinese-black e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                {goalDetail.admin_configured?.name}
                            </h1>
                        </header>

                        <div className="row gx-4 gy-4 align-items-start">

                            {/* Emergency fund plan */}
                            <div className="col-md-6">
                                <section className="retirement-goal-card e-bg-white e-border-radius-16 position-relative overflow-hidden pb-4">
                                    <img src={LeftPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-left d-none d-sm-block"
                                        draggable="false"
                                        alt="" />
                                    <img src={RightPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-right d-none d-sm-block"
                                        draggable="false"
                                        alt="" />

                                    <div className="retirement-goal-content position-relative px-sm-4">
                                        <header className="px-0 px-sm-5 pt-sm-3 text-center">
                                            <h2 className="mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-30px">
                                                Build your <span className="e-heading-font-ivy e-font-weight-400 e-text-cobalt-ink">emergency</span> fund
                                            </h2>
                                            <p className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-13 e-font-12-res">
                                                Stay prepared for life’s unexpected expenses with a financial safety net.
                                            </p>
                                        </header>

                                        {/* Projection */}
                                        <article className="retirement-projection e-border-1px-baby-blue e-border-radius-28 p-3 p-sm-4 mt-4">
                                            <div className="d-flex justify-content-between gap-3">
                                                <div>
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Invest
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{_formatToRupees(monthlyInvestment)}
                                                        <span className="ms-1 e-text-jet-gray e-font-weight-500 e-font-12 line-height-16px">
                                                            monthly
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Emergency fund
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{emergencyCorpusText.replace(" ", "")}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="retirement-timeline mt-4"
                                                aria-hidden="true">
                                                <span className="retirement-timeline-fill retirement-timeline-start"></span>
                                            </div>
                                            <p className="d-flex justify-content-between gap-2 mt-2 mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-14 e-font-12-res line-height-18px">
                                                <span>Start today</span>
                                                <span>
                                                    Achieve by <span className="e-text-ink-gray e-font-weight-500">{achieveByText}</span>
                                                </span>
                                            </p>

                                            {/* Surplus note */}
                                            <p className={`e-bg-white e-border-radius-24 e-box-shadow-soft-card p-3 mt-3 mb-0 e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px ${isSurplusEnough ? "e-text-eerie-black" : "e-text-fuzzy-wuzzy-red"}`}>
                                                Six months of your must pay expenses is <span className="e-font-weight-700">₹{emergencyCorpusText}</span>.{" "}
                                                {isSurplusEnough ?
                                                    <>Ready in {buildMonthsCount} months, and it fits in your monthly surplus of <span className="e-font-weight-700">₹{_formatToRupees(monthlySurplus)}</span>.</>
                                                    :
                                                    <>Your monthly surplus is <span className="e-font-weight-700">₹{_formatToRupees(monthlySurplus)}</span>, pick a longer build time.</>
                                                }
                                            </p>
                                        </article>
                                    </div>
                                </section>

                                {/* Inputs */}
                                <div className="d-flex flex-column gap-4 mt-3 px-4">
                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Salary per month
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={salary}
                                                maxValue={MAX_SALARY}
                                                errorMsg=""
                                                handleChange={_handleSalaryChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={SALARY_MIN}
                                                max={MAX_SALARY}
                                                step={SALARY_STEP}
                                                defaultValue={Number(salary)}
                                                handleChange={_handleSalaryChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                EMI’s per month
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={emi}
                                                maxValue={MAX_EMI}
                                                errorMsg=""
                                                handleChange={_handleEmiChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={0}
                                                max={MAX_EMI}
                                                step={EMI_STEP}
                                                defaultValue={Number(emi)}
                                                handleChange={_handleEmiChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                School fees per month
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={schoolFees}
                                                maxValue={MAX_SCHOOL_FEES}
                                                errorMsg=""
                                                handleChange={_handleSchoolFeesChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={0}
                                                max={MAX_SCHOOL_FEES}
                                                step={SCHOOL_FEES_STEP}
                                                defaultValue={Number(schoolFees)}
                                                handleChange={_handleSchoolFeesChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                House expenses per month
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={monthlyExpenses}
                                                maxValue={MAX_MONTHLY_EXPENSES}
                                                errorMsg=""
                                                handleChange={_handleMonthlyExpensesChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={MONTHLY_EXPENSES_MIN}
                                                max={MAX_MONTHLY_EXPENSES}
                                                step={MONTHLY_EXPENSES_STEP}
                                                defaultValue={Number(monthlyExpenses)}
                                                handleChange={_handleMonthlyExpensesChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="emergency-build-months"
                                            className="d-block mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                            Build it in
                                        </label>
                                        <SelectDropdown id="emergency-build-months"
                                            data={BUILD_MONTH_OPTIONS}
                                            selectedOption={selectedBuildMonths}
                                            onSelectOption={_handleSelectBuildMonths} />
                                    </div>

                                    {/* Save */}
                                    <PrimaryButton name="Save this goal"
                                        className="w-100 padding-12px-top padding-12px-bottom mt-3 e-font-12-res"
                                        loading={isSavingGoal}
                                        disabled={isSavingGoal}
                                        onPress={_handleSaveGoal} />

                                </div>

                            </div>
                        </div>
                    </div>
                }
            </div>

            {changeNameModal}

            {/* footer  */}
            <Footer />
        </>
    )
}
export default EmergencyFund;
